import type {
  CustomerType,
  GenericSearchParams,
  GetRecommendedSkusParams,
  ProductQueryOptions,
  RelatedProductsRequestType,
  ReviewFilterApplyParams,
  ShoppingListRequestParams,
  SponsoredProductsParams,
} from '@frodo/models/types';

// Server prefetches and client queries must share these keys for hydration to work.
export const QUERY_KEYS = {
  cart: () => ['cart-data'] as const,
  deletedCartItem: () => ['deleted-cart-item'] as const,
  userInfo: () => ['user-info'] as const,
  lastPurchased: () => ['last-purchased'] as const,
  search: (params: GenericSearchParams) => ['search', params] as const,
  searchPreview: (searchTerm: string, searchAutocompleteVariant?: string) =>
    ['search-preview', searchTerm, searchAutocompleteVariant] as const,
  sponsoredProducts: (params: SponsoredProductsParams) =>
    ['sponsored-products', params] as const,
  dsRecommendations: (params: GetRecommendedSkusParams) =>
    ['ds-recommendations-v1', params] as const,
  relatedProducts: ({
    sku,
    type,
  }: Pick<RelatedProductsRequestType, 'sku' | 'type'>) =>
    ['related-products', sku, ...type] as const,
  singleProduct: (
    sku: string,
    { isDetailed, isVideoEnabled, imageSize }: ProductQueryOptions = {},
  ) =>
    [
      'single-product-data',
      sku,
      isDetailed,
      isVideoEnabled,
      imageSize,
    ] as const,
  productList: (
    skuList: string[],
    { isDetailed, isVideoEnabled, imageSize }: ProductQueryOptions = {},
  ) =>
    [
      'list-product-data',
      skuList,
      isDetailed,
      isVideoEnabled,
      imageSize,
    ] as const,
  productVideo: (gtin: string) => ['demoup-video', gtin] as const,
  productInsurance: (sku: string) => ['product-insurance', sku] as const,
  productReviews: (
    productSku: string,
    store: string,
    filters: ReviewFilterApplyParams,
    perPage: number,
  ) => ['product-reviews', productSku, store, filters, perPage] as const,
  paymentMethod: (customerType: CustomerType) =>
    ['payment-method', customerType] as const,
  // called without arguments, these return the prefix that matches every variant
  shoppingLists: (
    params?: Pick<ShoppingListRequestParams, 'withItems' | 'imageSize'>,
  ) =>
    params
      ? (['shopping-lists', params] as const)
      : (['shopping-lists'] as const),
  shoppingListsContainingSku: (sku?: string) =>
    sku
      ? (['shopping-lists-containing-sku', sku] as const)
      : (['shopping-lists-containing-sku'] as const),
};

export const MUTATION_KEYS = {
  cartItemAdd: () => ['cart-item-add'] as const,
  cartItemRemove: () => ['cart-item-remove'] as const,
  cartItemQuantityUpdate: () => ['cart-item-quantity-update'] as const,
  cartInsuranceAdd: () => ['cart-insurance-add'] as const,
  cartInsuranceRemove: () => ['cart-insurance-remove'] as const,
  addVoucherCode: () => ['add-voucher-code'] as const,
  removeVoucherCode: () => ['remove-voucher-code'] as const,
  logout: () => ['logout'] as const,
  submitReview: (sku: string) => ['submit-review', sku] as const,
  createShoppingList: () => ['create-shopping-list'] as const,
  shoppingListAddItem: () => ['shopping-list-add-item'] as const,
  shoppingListAddItemToDefault: () =>
    ['shopping-list-add-item-to-default'] as const,
  shoppingListRemoveItem: () => ['shopping-list-remove-item'] as const,
  shoppingListBulkUpdate: () => ['shopping-list-bulk-update'] as const,
};
